import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import language from '../../variables/language';
import IconHome from '../../components/icons/IconHome';
import LessonDetailModal from '../students/LessonDetailModal';

const SKELETON_ROW_COUNT = 3;
const LIMIT = 10;
const MAX_NAMES_SHOWN = 3;
// Same 'ua' -> 'uk' mapping DailySchedule.jsx needs for real ICU weekday names.
const LUXON_LOCALE_BY_LANG = { en: 'en', ua: 'uk', pl: 'pl' };

function LessonRow({ item, lang, onOpen }) {
   const day = DateTime.fromFormat(item.date || '', 'yyyy-MM-dd').setLocale(LUXON_LOCALE_BY_LANG[language.lang] || 'en');
   const names = item.studentNames || [];
   const title = item.groupName || names[0] || '';
   const rest = item.groupName
      ? names.slice(0, MAX_NAMES_SHOWN).join(', ') + (names.length > MAX_NAMES_SHOWN ? ' +' + (names.length - MAX_NAMES_SHOWN) : '')
      : names.slice(1).join(', ');

   return (
      <div className='no-homework-row' onClick={() => onOpen(item.lessonId)} title={lang.lessonsNoHomeworkOpen}>
         <span className='no-homework-date'>
            <b>{day.isValid ? day.toFormat('dd') : '?'}</b>
            <i>{day.isValid ? day.toFormat('LLL') : ''}</i>
         </span>
         <div className='no-homework-main'>
            <div className='no-homework-top'>
               <span className='no-homework-title'>{title}</span>
               <span className='no-homework-time'>{item.start} – {item.end}</span>
            </div>
            <div className='no-homework-meta'>
               {day.isValid ? <span className='no-homework-weekday'>{day.toFormat('cccc')}</span> : ''}
               {rest ? <span className='no-homework-people'>{rest}</span> : ''}
               <span className='no-homework-chip'>{lang.lessonsNoHomeworkChip}</span>
            </div>
         </div>
      </div>
   );
}

// Dashboard tile under RecentHomework: per student, their last held lesson
// when it has no homework attached (GET /api/dashboard/lessons-without-
// homework). Click opens the shared LessonDetailModal - the same one
// DailySchedule uses - where the teacher can add the homework; the list is
// refetched when the modal closes, so a lesson that just got homework drops
// out of it.
export default function LessonsWithoutHomework() {
   const lang = language[language.lang];
   const [data, setData] = useState([]);
   const [status, setStatus] = useState('loading');
   const [selectedLessonId, setSelectedLessonId] = useState(null);
   const [modalOpen, setModalOpen] = useState(false);

   const load = useCallback(() => {
      axios.get('/api/dashboard/lessons-without-homework?limit=' + LIMIT, { withCredentials: true })
         .then(response => setData(Array.isArray(response.data) ? response.data : []))
         .catch(err => console.error(err))
         .finally(() => setStatus('done'));
   }, []);

   useEffect(() => { load(); }, [load]);

   const open = (lessonId) => { setSelectedLessonId(lessonId); setModalOpen(true); };
   const close = () => { setModalOpen(false); load(); };

   return (
      <div className='no-homework-panel dashboard-component'>
         <div className='header-dahs-elem'>
            <span className='no-homework-header'>
               <IconHome style={{ width: 30, height: 30, fill: 'var(--dashboard-header-icon-color)' }} />
               <p className='txt-c'>{lang.lessonsNoHomeworkTitle}</p>
            </span>
            {status === 'done' ? (
               <span className={'pending-approvals-count' + (data.length > 0 ? ' has-items' : '')}>{data.length}</span>
            ) : ''}
         </div>
         <div className='no-homework-list scrooll'>
            {status === 'loading' ? (
               Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                  <div key={i} className='skeleton-block no-homework-skeleton'></div>
               ))
            ) : ''}
            {status === 'done' && data.length === 0 ? <p className='pending-approvals-empty'>{lang.lessonsNoHomeworkEmpty}</p> : ''}
            {status === 'done' ? data.map(item => <LessonRow key={item.lessonId} item={item} lang={lang} onOpen={open} />) : ''}
         </div>
         <LessonDetailModal isOpen={modalOpen} lessonId={selectedLessonId}
            onClose={close} onSaved={close} onParticipantsChanged={load} />
      </div>
   );
}
